import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import axios from 'axios';
import { HiChevronDown, HiX } from 'react-icons/hi'; // React Icons for toggle and close icons
import PrintMatrimonialClassifieds from './PrintMatrimonialClassifieds'; // Adjust path as needed
import { API_URL } from '../../creds';
import { formatPhoneNumber } from '../NumberFormatter';
import Username from '../Username';
import CRNSchemaModal from './CRNSchemaModal';

const PAGE_SIZES = [
    { label: "A4", value: "A4", heightCm: 29.7 },
    { label: "A3", value: "A3", heightCm: 42 },
    { label: "Double A3", value: "DOUBLE_A3", heightCm: 52 },
    { label: "Custom Long", value: "LONG", heightCm: 52 },
];

function PrintPreview() {
    const [showModal, setShowModal] = useState(false);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [genders, setGenders] = useState([]);
    const [selectedGender, setSelectedGender] = useState('');
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [subcategories, setSubcategories] = useState([]);
    const [selectedSubcategory, setSelectedSubcategory] = useState('');
    // eslint-disable-next-line
    const [classifieds, setClassifieds] = useState([]);
    const [filteredClassifieds, setFilteredClassifieds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isCRNSchemaModalOpen, setIsCRNSchemaModalOpen] = useState(false);
    const [selectedCRNItem, setSelectedCRNItem] = useState(null);
    const [selectedMax_columns, setSelectedMax_columns] = useState(8);
    const [selectedPageSize, setSelectedPageSize] = useState("DOUBLE_A3");
    const [maxPageHeightCm, setMaxPageHeightCm] = useState(52);

    // Fetch dropdown data (genders, categories, subcategories)
    useEffect(() => {
        const fetchDropdownData = async () => {
            try {
                const response = await axios.get(`${API_URL}/matrimonial/dropdowns`);
                setGenders(response.data.genders || []);
                setCategories(response.data.categories || []);
                setSubcategories(response.data.subcategories || []);
            } catch (err) {
                setError('Failed to fetch dropdown data');
            }
        };
        fetchDropdownData();
    }, []);

    // Filter subcategories based on selected category
    const filteredSubcategories = selectedCategory
        ? subcategories.filter((sub) => sub.category === selectedCategory)
        : subcategories;

    // Handle form submission to fetch matrimonial data
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
      
        try {
          const params = {};
      
          if (fromDate) params.from_date = fromDate.toISOString().split('T')[0];
          if (toDate) params.to_date = toDate.toISOString().split('T')[0];
          if (selectedGender) params.gender = selectedGender;
          if (selectedCategory) params.category = selectedCategory;
      
          // 🔹 NEW: max columns (1–8)
          if (selectedMax_columns) {
            params.max_columns = selectedMax_columns;
          }
      
          // 🔹 NEW: page size & max height in cm
          if (selectedPageSize) {
            params.page_size = selectedPageSize;
          }
      
          if (maxPageHeightCm) {
            params.max_height_cm = maxPageHeightCm; // example: 52
          }
      
          const response = await axios.get(`${API_URL}/matrimonial`, { params });
      
          setClassifieds(response.data);
          setFilteredClassifieds(response.data);
      
          console.log("FETCH DATA Matrimonials:", {
            params,
            data: response.data,
          });
      
          setShowModal(false);
        } catch (err) {
          setError(
            err.response?.data?.detail || "Failed to fetch matrimonial data"
          );
        } finally {
          setLoading(false);
        }
      };
      
    // Toggle modal visibility
    const toggleModal = () => {
        setShowModal(!showModal);
    };

    // Handle CRN click to open modal
    const handleCRNClick = (item) => {
        setSelectedCRNItem(item); // Set the selected item for the modal
        setIsCRNSchemaModalOpen(true);
    };

    // Close CRN schema modal
    const handleCloseCRNSchemaModal = () => {
        setIsCRNSchemaModalOpen(false);
        setSelectedCRNItem(null);
    };

    // Combine hline1, hline2, hline3 for Heading column
    const combineHlines = (item) => {
        const parts = [item.hline1, item.hline2, item.hline3].filter(Boolean);
        return parts.length > 0 ? parts.join('\n') : '-';
    };

    // Table headers configuration
    const headers = [
        { key: 'index', label: 'SN', width: 'w-[4%]', minWidth: 'min-w-[20px]' },
        { key: 'crn', label: 'CRN', width: 'w-[12%]', minWidth: 'min-w-[80px]' },
        { key: 'gender', label: 'Gender', width: 'w-[8%]', minWidth: 'min-w-[80px]' },
        { key: 'category', label: 'Category', width: 'w-[9%]', minWidth: 'min-w-[80px]' },
        { key: 'subcategory', label: 'Subcategory', width: 'w-[11%]', minWidth: 'min-w-[90px]' },
        { key: 'heading', label: 'Heading', width: 'w-[12%]', minWidth: 'min-w-[90px]' },
        { key: 'contact', label: 'Contact', width: 'w-[10%]', minWidth: 'min-w-[80px]' },
        { key: 'agency', label: 'Agency', width: 'w-[8%]', minWidth: 'min-w-[60px]' },
    ];

    const handlePageSizeChange = (e) => {
        const size = PAGE_SIZES.find(p => p.value === e.target.value);
        setSelectedPageSize(size.value);
        setMaxPageHeightCm(size.heightCm);
      };
      

    return (
        <>
            <Username />
            <div className="container mx-auto p-6 bg-gray-50 min-h-screen">
                {/* Prepare Data Button (Left-aligned with Icon) */}
                <div className="flex justify-start mb-6">
                    <button
                        onClick={toggleModal}
                        className="
                        text-xl 
                        group 
                        bg-gradient-to-r from-blue-600 to-purple-600 
                        text-white 
                        hover:from-purple-600 hover:to-blue-600  
                        font-semibold 
                        py-3 
                        px-6 
                        rounded-full 
                        shadow-lg 
                        hover:shadow-2xl 
                        transition-all 
                        duration-300 
                        flex 
                        items-center 
                        gap-2
                      "
                    >
                        <HiChevronDown className="w-5 h-5 mr-2" />
                        Prepare Data
                    </button>
                </div>

                {/* Modal Form */}
                {showModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-xl font-semibold text-gray-800">Filter Matrimonial Data</h2>
                                <button onClick={toggleModal} className="text-gray-600 hover:text-gray-800" title="Close">
                                    <HiX className="w-6 h-6" />
                                </button>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                                        <DatePicker
                                            selected={fromDate}
                                            onChange={(date) => setFromDate(date)}
                                            dateFormat="yyyy-MM-dd"
                                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholderText="Select from date"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                                        <DatePicker
                                            selected={toDate}
                                            onChange={(date) => setToDate(date)}
                                            dateFormat="yyyy-MM-dd"
                                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholderText="Select to date"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                                        <select
                                            value={selectedGender}
                                            onChange={(e) => setSelectedGender(e.target.value)}
                                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        >
                                            <option value="">All Genders</option>
                                            {genders.map((gender) => (
                                                <option key={gender} value={gender}>
                                                    {gender}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                                        <select
                                            value={selectedCategory}
                                            onChange={(e) => {
                                                setSelectedCategory(e.target.value);
                                                setSelectedSubcategory(''); // Reset subcategory
                                            }}
                                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        >
                                            <option value="">All Categories</option>
                                            {categories.map((category) => (
                                                <option key={category} value={category}>
                                                    {category}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Subcategory</label>
                                        <select
                                            value={selectedSubcategory}
                                            onChange={(e) => setSelectedSubcategory(e.target.value)}
                                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                                            disabled={!selectedCategory}
                                        >
                                            <option value="">All Subcategories</option>
                                            {filteredSubcategories.map((subcategory) => (
                                                <option key={subcategory.subcategory} value={subcategory.subcategory}>
                                                    {subcategory.subcategory}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Max Columns</label>
                                        <select
                                            value={selectedMax_columns}
                                            onChange={(e) => setSelectedMax_columns(Number(e.target.value))}
                                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                                        >
                                            {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                                                <option key={num} value={num}>
                                                    {num}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Page Size
                                        </label>

                                        <select
                                            value={selectedPageSize}
                                            onChange={handlePageSizeChange}
                                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        >
                                            {PAGE_SIZES.map((page) => (
                                                <option key={page.value} value={page.value}>
                                                    {page.label} ({page.heightCm} cm)
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                </div>

                                <div className="mt-6 flex justify-end gap-4">
                                    <button
                                        type="button"
                                        onClick={toggleModal}
                                        className="px-4 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 transition-colors duration-200"
                                    >
                                        Close
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 disabled:bg-blue-300"
                                        disabled={loading}
                                    >
                                        {loading ? 'Loading...' : 'Fetch Data'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Error Message */}
                {error && (
                    <div className="max-w-2xl mb-6 p-4 bg-red-100 text-red-700 rounded-lg">{error}</div>
                )}

                {/* Table and PDF Button */}
                {filteredClassifieds?.columns?.length > 0 && (
                    <div className="max-w-full">
                        <div className="flex justify-end py-2">
                            <button className="p-2 rounded text-white text-3xl">
                                <PrintMatrimonialClassifieds title="Print Classified Preview" data={filteredClassifieds} />
                            </button>
                        </div>
                        <div className="w-full overflow-x-hidden">
                            {/* Table for larger screens */}
                            <div className="hidden md:block">
                                <div className="mx-auto">
                                    <table className="w-full bg-white border border-gray-300 shadow-md rounded-lg table-fixed">
                                        <thead>
                                            <tr className="bg-gray-100 text-gray-900 text-left font-bold">
                                                {headers.map(({ key, label, width }) => (
                                                    <th
                                                        key={key}
                                                        className={`px-2 py-2 text-[12px] whitespace-nowrap sm:whitespace-pre-wrap sm:break-words border border-gray-300 ${width}`}
                                                    >
                                                        {label}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredClassifieds?.columns?.length > 0 ? (
                                                filteredClassifieds.columns.map((col, colIndex) => (
                                                    col.cards.map((item, cardIndex) => (
                                                        <tr
                                                            key={`${col.page}-${col.column + 1}-${item.id}`}
                                                            className="border-b hover:bg-gray-50"
                                                        >
                                                            {/* Sr No */}
                                                            <td className="py-2 px-2 text-[12px] border border-gray-300">
                                                                {colIndex + 1}.{cardIndex + 1}
                                                            </td>

                                                            {/* CRN */}
                                                            <td
                                                                className="py-2 px-2 text-[12px] border border-gray-300 cursor-pointer hover:text-blue-600"
                                                                onClick={() => handleCRNClick(item)}
                                                            >
                                                                {item.crn
                                                                    ? `${String(item.crn).slice(0, 4)}-${String(item.crn).slice(4, 8)}-${String(item.crn).slice(8, 12)}-${String(item.crn).slice(12)}`
                                                                    : '-'}
                                                            </td>

                                                            <td className="py-2 px-2 text-[12px] border border-gray-300">
                                                                {item.gender || '-'}
                                                            </td>

                                                            <td className="py-2 px-2 text-[12px] border border-gray-300">
                                                                {item.category || '-'}
                                                            </td>

                                                            <td className="py-2 px-2 text-[12px] border border-gray-300">
                                                                {item.subcategory || '-'}
                                                            </td>

                                                            <td className="py-2 px-2 text-[12px] border border-gray-300">
                                                                {combineHlines(item)}
                                                            </td>

                                                            <td className="py-2 px-2 text-[12px] border border-gray-300">
                                                                {formatPhoneNumber(item.mobile || item.whatsapp || item.email || '-')}
                                                            </td>

                                                            <td className="py-2 px-2 text-[12px] border border-gray-300">
                                                                {item.agency || '-'}
                                                            </td>
                                                        </tr>
                                                    ))
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={headers.length} className="py-6 text-center text-gray-500">
                                                        No classifieds found.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>

                                    </table>
                                </div>
                            </div>

                            {/* Accordion View for mobile screens */}
                            <div className="md:hidden flex flex-col gap-4 p-2">
                                {filteredClassifieds.length > 0 ? (
                                    filteredClassifieds.map((item, index) => (
                                        <details key={item.id} className="bg-white border border-gray-300 rounded-lg shadow-md p-3">
                                            <summary className="font-semibold text-gray-700 cursor-pointer list-none text-[clamp(12px,2.5vw,14px)] truncate">
                                                S.No.: {index + 1} - {item.category || '-'}
                                            </summary>
                                            <div className="mt-2 grid grid-cols-1 gap-2 text-gray-600 text-[clamp(11px,2.2vw,13px)]">
                                                <div className="truncate">
                                                    <span className="font-semibold">CRN No.:</span>{' '}
                                                    <span
                                                        className="cursor-pointer hover:text-blue-600"
                                                        onClick={() => handleCRNClick(item)}
                                                    >
                                                        {item.crn
                                                            ? `${String(item.crn).slice(0, 4)}-${String(item.crn).slice(4, 8)}-${String(item.crn).slice(8, 12)}-${String(item.crn).slice(12)}`
                                                            : '-'}
                                                    </span>
                                                </div>
                                                <div className="truncate">
                                                    <span className="font-semibold">Gender:</span> {item.gender || '-'}
                                                </div>
                                                <div className="truncate">
                                                    <span className="font-semibold">Subcategory:</span> {item.subcategory || '-'}
                                                </div>
                                                <div className="truncate">
                                                    <span className="font-semibold">Heading:</span> {combineHlines(item)}
                                                </div>
                                                <div className="truncate">
                                                    <span className="font-semibold">Contact:</span>{' '}
                                                    {formatPhoneNumber(item.mobile || item.whatsapp || item.email || '-')}
                                                </div>
                                                <div className="truncate">
                                                    <span className="font-semibold">Agency:</span> {item.agency || '-'}
                                                </div>
                                            </div>
                                        </details>
                                    ))
                                ) : (
                                    <div className="text-center text-gray-500 py-6 text-[clamp(12px,2.5vw,14px)]">
                                        No classifieds found.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
                <CRNSchemaModal
                    isOpen={isCRNSchemaModalOpen}
                    onClose={handleCloseCRNSchemaModal}
                    item={selectedCRNItem}
                />
            </div>
        </>
    );
}

export default PrintPreview;